<template>
  <div class="st-file-picker">
    <StFormGroup
      :for-id="id"
      :label="selectedFiles.length > 0 ? 'Uppladdad fil' : 'Ladda upp fil'"
      :required="!selectedFiles.length > 0 && required"
      :description="selectedFiles.length > 0 ? '' : selectDescription"
      descriptionId="filePickerDesc"
    >
      <div
        :id="`${id}-filePickerContainer`"
        :class="containerClasslist"
        aria-describedby="filePickerDesc"
        @drop.stop.prevent="validateFiles($event.dataTransfer.files)"
        @dragover.stop.prevent="toggleActive(true)"
        @dragleave.stop.prevent="toggleActive(false)"
        @dragenter.stop.prevent="toggleActive(true)"
      >
        <transition
          name="fade"
          mode="out-in"
          @after-enter="handleFocus"
          style="width: 100%"
        >
          <StSpinner
            v-if="isLoading"
            :small="isSmall"
            :loading-message="loadingDescription"
          />
          <div v-else-if="selectedFiles.length > 0">
            <SelectedFile
              v-for="(file, index) in selectedFiles"
              :key="index"
              :file="file"
              :file-url="fileUrl[index]"
              :focus-on-mount="focusOnMount"
              :remove-btn-text="removeBtnText"
              @file-removed="emit('onFileRemove', index)"
            />
            <div class="st-file-picker__container__select">
              <FileInput
                :id="id"
                ref="filePickerInput"
                :accepted-filetypes="acceptedFiletypes"
                @file-selected="validateFiles($event.target.files)"
                :allow-multiple="allowMultiple"
                :aria-describedby="ariaDescribedby"
                :upload-btn-text="uploadBtnText"
              />
              <span class="st-file-picker-select__desc">
                {{ uploadDescriptionText }}
              </span>
            </div>
          </div>
          <div v-else class="st-file-picker__container__select">
            <FileInput
              :id="id"
              ref="filePickerInput"
              :accepted-filetypes="acceptedFiletypes"
              @file-selected="validateFiles($event.target.files)"
              :allow-multiple="allowMultiple"
              :aria-describedby="ariaDescribedby"
              :upload-btn-text="uploadBtnText"
            />
            <span class="st-file-picker-select__desc">
              {{ uploadDescriptionText }}
            </span>
          </div>
        </transition>
      </div>
    </StFormGroup>
    <transition name="error">
      <span
        v-if="hasError"
        class="st-file-picker__error"
        role="alert"
        aria-live="assertive"
        :aria-atomic="true"
      >
        <StIcon
          name="warning-triangle"
          size="small"
          :icon-props="{ color: 'var(--danger-color-primary)' }"
        />
        {{ errorMessage }}
      </span>
    </transition>
  </div>
</template>
<script setup>
import { computed, ref } from "vue";
import SelectedFile from "./SelectedFile.vue";
import { StFormGroup, StSpinner, StIcon } from "@/index.js";
import FileInput from "./FileInputComponent.vue";

const emit = defineEmits([
  "onFileRemove",
  "onFileSelect",
  "clearFiles",
  "maxFilesAmountError",
  "maxFileSizeError",
  "invalidFileTypeError",
]);

const props = defineProps({
  acceptedFiletypes: {
    type: Array,
    default: () => [],
  },
  allowMultiple: {
    type: Boolean,
    default: false,
  },
  maxFileSizeMB: {
    type: Number,
    default: 5,
  },
  maxAmountOfFiles: {
    type: Number,
    default: 5,
  },
  selectedFiles: {
    type: Array,
    default: () => [],
  },
  isLoading: Boolean,
  selectDescription: String,
  loadingDescription: String,
  required: Boolean,
  fileUrl: {
    type: Array,
    default: () => [],
  },
  isSmall: Boolean,
  focusOnMount: Boolean,
  externalErrorMessage: {
    type: String,
    default: "",
  },
  maxFileSizeMBErrorMsg: {
    type: String,
    default: "",
  },
  maxAmountOfFilesErrorMsg: {
    type: String,
    default: "",
  },
  invalidFileTypeErrorMsg: {
    type: String,
    default: "",
  },
  id: {
    type: String,
    default: "",
  },
  ariaDescribedby: {
    type: String,
  },
  useExternalErrorStore: {
    type: Boolean,
    default: false,
  },
  removeBtnText: {
    type: String,
    default: "Ersätt fil",
  },
  uploadBtnText: {
    type: String,
    default: "Välj fil från datorn",
  },
  uploadDescriptionText: {
    type: String,
    default: "eller dra och släpp din fil inuti det streckade området",
  },
});

const filePickerInput = ref(null);
const isDraggedOver = ref(false);
const internalErrorMessage = ref("");

const errorMessage = computed(
  () => internalErrorMessage.value || props.externalErrorMessage,
);
const hasError = computed(() => Boolean(errorMessage.value));

const containerClasslist = computed(() => {
  const container = "st-file-picker__container";
  return [
    container,
    props.isSmall && `${container}--small`,
    hasError.value && `${container}--invalid`,
    isDraggedOver.value && `${container}--active`,
  ];
});

const toggleActive = (value) => {
  if (!props.selectedFiles.length > 0) isDraggedOver.value = value;
};

const validateFiles = (files) => {
  toggleActive(false);
  emit("clearFiles");
  internalErrorMessage.value = "";

  if (files.length > props.maxAmountOfFiles) {
    props.useExternalErrorStore
      ? emit("maxFilesAmountError")
      : (internalErrorMessage.value = props.maxAmountOfFilesErrorMsg);
    return;
  }

  for (let i = 0; i < files.length; i++) {
    if (
      props.acceptedFiletypes.length &&
      !props.acceptedFiletypes.includes(files[i].type)
    ) {
      props.useExternalErrorStore
        ? emit("invalidFileTypeError")
        : (internalErrorMessage.value = props.invalidFileTypeErrorMsg);
      return;
    } else if (files[i].size > props.maxFileSizeMB * 1024 * 1024) {
      props.useExternalErrorStore
        ? emit("maxFileSizeError")
        : (internalErrorMessage.value = props.maxFileSizeMBErrorMsg);
      return;
    }
  }
  emit("onFileSelect", files);
};

const handleFocus = () => {
  if (!props.selectedFiles.length > 0 && !props.isLoading) {
    filePickerInput.value.fileInputLabel.$el.focus();
  }
};
</script>
<style lang="scss" scoped>
@import "../../scss/main.scss";
.st-file-picker {
  &__container {
    min-height: rem-calc(210);
    background: var(--container-color-tertiary);
    border: dashed rem-calc(1) var(--text-color-secondary);
    color: var(--text-color-primary);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    border-radius: rem-calc(8);

    &--small {
      min-height: rem-calc(120);
    }

    &--active {
      background: var(--background-color-quaternary);
      border: solid rem-calc(1) var(--border-color-primary);
    }

    &--invalid {
      border: solid rem-calc(1) var(--danger-color-primary);
    }

    &__select {
      text-align: center;
      padding: rem-calc(16);
      margin: rem-calc(32) 0;
      & > :not(:last-child) {
        margin-bottom: rem-calc(16);
      }
    }
  }

  &__error {
    display: flex;
    margin-top: rem-calc(4);
    font-size: rem-calc(14);
    font-weight: var(--font-weight-semi-bold);
    color: var(--danger-color-primary);

    & > :first-child {
      margin-right: rem-calc(4);
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.error-enter-active,
.error-leave-active {
  transition: opacity 0.2s ease;
}

.error-enter-from,
.error-leave-to {
  opacity: 0;
}
</style>
